import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import PeopleIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import { labAPI } from '../../services/api';
import { formatLocalDate } from '../../utils/formatLocalDate';
import { useAuth } from '../../context/AuthContext';
import { useNotify } from '../../context/NotifyContext';
import AccountSettingsPanel from '../AccountSettingsPanel';
import { MessagingConversationListItem, MessagingMessageRow } from '../MessagingUI';
import { userDisplayLabel } from '../../utils/userDisplayLabel';
import { useMessagingInbox } from '../../hooks/useMessagingInbox';
import { formatLocalDateTime } from '../../utils/formatLocalDate';
import { filterBySearch } from '../../utils/tableSearch';
import TableToolbar from '../TableToolbar';
import GradientSpinner from '../GradientSpinner';
import '../../App.css';

const LabEmployeeDashboard = ({ activeTab: propTab }) => {
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotify();
  const [activeTab, setActiveTab] = useState(propTab || 'overview');
  const [analytics, setAnalytics] = useState(null);
  const [labTasks, setLabTasks] = useState([]);

  const labSupervisorId = user?.labId?._id || user?.labId;

  const {
    conversations,
    messages: chatMessages,
    selectedConvId: selectedConv,
    setSelectedConvId: setSelectedConv,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading: messagingLoading
  } = useMessagingInbox({
    enabled: activeTab === 'messaging',
    filterConversation: (c) => c.otherParty?.role === 'lab'
  });
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [empTaskSearch, setEmpTaskSearch] = useState('');
  const [empTaskOverviewSearch, setEmpTaskOverviewSearch] = useState('');

  useEffect(() => {
    if (propTab) setActiveTab(propTab);
  }, [propTab]);

  useEffect(() => {
    labAPI.getDashboard().then(res => {
      if (res.data) setAnalytics(res.data);
    }).catch(() => {});
  }, []);

  const loadTasks = useCallback(() => {
    labAPI.getLabTasks().then((res) => {
      setLabTasks(res.data?.tasks || []);
    }).catch(() => setLabTasks([]));
  }, []);

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'my-tasks' || activeTab === 'calendar') loadTasks();
  }, [activeTab, loadTasks]);

  const updateTaskStatus = async (taskId, status) => {
    try {
      const { data } = await labAPI.updateLabTask(taskId, { status });
      setLabTasks((prev) => prev.map((t) => (t._id === taskId ? data.task : t)));
    } catch {
      /* ignore */
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'calendar': return 'Calendar';
      case 'my-tasks': return 'My Tasks';
      case 'analytics': return 'Analytics';
      case 'messaging': return 'Messages';
      case 'profile': return 'Profile';
      default: return 'Overview';
    }
  };

  const filteredEmpTasks = useMemo(
    () =>
      filterBySearch(labTasks, empTaskSearch, (t) => [t.title, t.description, t.status, t.priority]),
    [labTasks, empTaskSearch]
  );

  const filteredOverviewTasks = useMemo(
    () =>
      filterBySearch(labTasks, empTaskOverviewSearch, (t) => [t.title, t.status]),
    [labTasks, empTaskOverviewSearch]
  );

  const getDaysInMonth = (year, month) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];
    const startPad = first.getDay();
    for (let i = 0; i < startPad; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(d);
    return days;
  };

  const getTasksForDate = (day) => {
    if (!day) return [];
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return labTasks.filter((t) => {
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate);
      const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return ds === dateStr;
    });
  };


  return (
    <div>
      {activeTab !== 'overview' && (
        <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 8px 0', paddingTop: '0' }}>
          {getPageTitle()}
        </h1>
      )}

      {activeTab === 'overview' && (
        <>
          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(51, 255, 217, 0.1), rgba(51, 255, 217, 0.05))', border: '1px solid rgba(51, 255, 217, 0.2)' }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>My Tasks</h3>
            <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--logo-cyan-teal)', margin: '16px 0 0 0' }}>{labTasks.length}</p>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Assigned by lab</p>
            <Link to="/dashboard?tab=my-tasks" className="btn btn-outline" style={{ marginTop: '12px', textDecoration: 'none', display: 'inline-block' }}>View all</Link>
          </div>
          <div className="card" style={{ marginTop: '24px' }}>
            <h3 className="card-title" style={{ color: 'var(--logo-cyan-teal)' }}>Quick links</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px', marginTop: '16px' }}>
              <Link to="/dashboard?tab=calendar" className="btn btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <EventIcon sx={{ fontSize: 20 }} /> Calendar
              </Link>
              <Link to="/dashboard?tab=my-tasks" className="btn btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 20 }} /> Tasks
              </Link>
              <Link to="/dashboard?tab=messaging" className="btn btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <ChatIcon sx={{ fontSize: 20 }} /> Messages
              </Link>
              <Link to="/dashboard?tab=profile" className="btn btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <PeopleIcon sx={{ fontSize: 20 }} /> Profile
              </Link>
            </div>
          </div>
        </>
      )}

      {activeTab === 'calendar' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Task Calendar</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>Track your assigned tasks by date</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button type="button" className="btn btn-outline" style={{ padding: '6px 12px' }} onClick={() => { const d = new Date(calendarYear, calendarMonth - 1); setCalendarMonth(d.getMonth()); setCalendarYear(d.getFullYear()); }}>←</button>
              <span style={{ fontWeight: '600', minWidth: '140px', textAlign: 'center' }}>
                {new Date(calendarYear, calendarMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
              <button type="button" className="btn btn-outline" style={{ padding: '6px 12px' }} onClick={() => { const d = new Date(calendarYear, calendarMonth + 1); setCalendarMonth(d.getMonth()); setCalendarYear(d.getFullYear()); }}>→</button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', padding: '8px', textAlign: 'center' }}>{d}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
            {getDaysInMonth(calendarYear, calendarMonth).map((day, i) => {
              const tasks = getTasksForDate(day);
              return (
                <div key={i} style={{ minHeight: '80px', padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-tertiary)' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px' }}>{day || ''}</div>
                  {tasks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {tasks.map(t => (
                        <div key={t._id} style={{ fontSize: '0.6875rem', padding: '4px 6px', background: 'rgba(51, 255, 217, 0.15)', borderRadius: '4px', borderLeft: '3px solid var(--logo-cyan-teal)' }} title={t.title}>
                          {t.title?.slice(0, 20)}{t.title?.length > 20 ? '…' : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '16px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '0.9375rem' }}>Upcoming tasks</h3>
            <TableToolbar
              compact
              search={empTaskOverviewSearch}
              onSearchChange={setEmpTaskOverviewSearch}
              placeholder="Filter upcoming…"
              resultCount={filteredOverviewTasks.length}
              totalCount={labTasks.length}
            />
            {filteredOverviewTasks.map(t => (
              <div key={t._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ fontSize: '0.875rem' }}>{t.title}</span>
                <span className={`badge ${t.status === 'done' ? 'badge-success' : t.status === 'in_progress' ? 'badge-info' : 'badge-warning'}`}>
                  {t.dueDate ? formatLocalDate(t.dueDate) : 'No date'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'my-tasks' && (
        <div className="card">
          <h2 className="card-title" style={{ marginBottom: '16px' }}>Assigned tasks</h2>
          <TableToolbar
            search={empTaskSearch}
            onSearchChange={setEmpTaskSearch}
            placeholder="Search tasks…"
            resultCount={filteredEmpTasks.length}
            totalCount={labTasks.length}
          />
          <div style={{ display: 'grid', gap: '16px' }}>
            {filteredEmpTasks.map((task) => (
              <div key={task._id} className="card" style={{ padding: '16px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ color: 'var(--logo-cyan-teal)', margin: 0, fontSize: '1.0625rem' }}>{task.title}</h3>
                  <select
                    className="form-select"
                    style={{ minWidth: '140px', fontSize: '0.8125rem' }}
                    value={task.status}
                    onChange={(e) => updateTaskStatus(task._id, e.target.value)}
                  >
                    <option value="todo">To do</option>
                    <option value="in_progress">In progress</option>
                    <option value="done">Done</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                {task.description && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{task.description}</p>
                )}
                <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Due: {task.dueDate ? formatLocalDate(task.dueDate) : '—'} · Priority: {task.priority} · {task.roleTag?.replace('_', ' ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-3" style={{ gap: '24px' }}>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Tasks Completed by Month</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.tasksByMonth || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.month}</span>
                  <span style={{ fontWeight: '600', color: 'var(--logo-cyan-teal)' }}>{d.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h3 className="card-title" style={{ marginBottom: '12px' }}>Tasks by Status</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(analytics?.tasksByStatus || [])?.map((d, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{d.status}</span>
                  <span className="badge badge-info">{d.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'messaging' && (
        <div className="card messaging-card">
          <h2 className="card-title">Messages</h2>
          {labSupervisorId && (
            <div style={{ marginBottom: '12px' }}>
              <button type="button" className="btn btn-outline" onClick={() => startConversation(labSupervisorId)}>
                Message your lab
              </button>
              {messagingLoading && (
                <span style={{ marginLeft: '12px', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <GradientSpinner size={22} />
                  Loading…
                </span>
              )}
            </div>
          )}
          <div className="messaging-layout">
            <div className="messaging-conversation-list">
              {conversations.map((conv) => (
                <MessagingConversationListItem
                  key={conv._id}
                  conv={conv}
                  selectedConvId={selectedConv}
                  onSelect={setSelectedConv}
                  title={userDisplayLabel(conv.otherParty)}
                />
              ))}
            </div>
            <div className="messaging-thread">
              {selectedConv ? (
                <>
                  <div className="messaging-messages">
                    {chatMessages.map((msg) => (
                      <MessagingMessageRow key={msg._id} msg={msg} meUser={user} formatLocalDateTime={formatLocalDateTime} />
                    ))}
                  </div>
                  <form onSubmit={sendMessage} className="messaging-composer">
                    <input type="file" accept="image/*,.pdf" multiple style={{ maxWidth: '120px', padding: '4px', fontSize: '0.75rem' }} title="Attach" disabled />
                    <input type="text" className="form-input" placeholder="Type a message..." value={messageText} onChange={(e) => setMessageText(e.target.value)} />
                    <button type="submit" className="btn btn-primary" disabled={!messageText.trim()}>Send</button>
                  </form>
                </>
              ) : (
                <div className="messaging-empty">Select a conversation or message your lab</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <>
          <AccountSettingsPanel
            user={user}
            refreshUser={refreshUser}
            onMessage={(m) => {
              if (!m) return;
              const s = String(m).trim();
              if (/^error\s*:/i.test(s)) showError(s.replace(/^error\s*:\s*/i, ''));
              else showSuccess(s);
            }}
            after={(
              <div className="card account-settings-card">
                <h2 className="card-title" style={{ marginBottom: '16px' }}>Account</h2>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>Lab employee account. Managed by your lab.</p>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div><span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Role</span><p style={{ margin: '4px 0 0 0', fontWeight: '500' }}>Lab Employee</p></div>
                  <div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Lab</span>
                    <p style={{ margin: '4px 0 0 0', fontWeight: '500', color: 'var(--logo-cyan-teal)' }}>
                      {user?.labId?.labName || user?.labId?.name || '—'}
                    </p>
                  </div>
                  <div><span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Permissions</span><p style={{ margin: '4px 0 0 0', fontSize: '0.875rem' }}>View reports, Manage tasks, View jobs</p></div>
                </div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
};

export default LabEmployeeDashboard;
